<template>
	<div class="row">
		<div class="col-12 col-lg-5 col-xl-4">

			<h6 class="text-uppercase text-muted">Filename/Version</h6>
			<div v-if="creativeAsset.media.length" class="dropdown mb-4">
				<a class="py-3 btn btn-white dropdown-toggle text-start d-flex align-items-center" href="#" role="button" data-bs-toggle="dropdown">
					<div>{{ strLimit(selectedMedia.original_name, 20) }}</div>
					<div class="badge bg-light mb-n1 ms-auto">
						v{{ selectedMedia.version }}
					</div>
				</a>
				<div class="dropdown-menu w-100 bg-lighter">
					<h6 class="dropdown-header">Versions</h6>
					<a v-for="(version, index) in creativeAsset.media"
						href="#"
						@click.prevent="selectedMedia = version; resetPolling();"
						:class="selectedMedia.id == version.id
							? 'dropdown-item py-3 active bg-white w-100 d-flex align-items-center'
							: 'dropdown-item py-3 w-100 d-flex align-items-center'">
						{{ strLimit(version.original_name, 20) }}
						<span class="badge bg-light ms-auto">
							v{{ version.version }}
						</span>
					</a>
				</div>
			</div><!--/.dropdown-->
			<div v-else class="d-flex align-items-center">
				<div class="fw-bold">{{ selectedMedia.original_name }}</div>
				<div class="badge bg-light ms-2">v{{ selectedMedia.version }}</div>
			</div>

			<div v-if="selectedMedia.uploader">
				<h6 class="text-uppercase text-muted">Uploaded</h6>
				<p class="small mb-4">
					{{ formatDate(selectedMedia.created_at) }}
					by {{ selectedMedia.uploader.name }}
				</p>
			</div>

			<div v-if="selectedMedia.width">
				<h6 class="text-uppercase text-muted">Dimensions</h6>
				<p class="small mb-4">
					<span v-if="Math.abs(selectedMedia.video_rotation) == 90">
						{{ selectedMedia.height }} x {{ selectedMedia.width }}
					</span>
					<span v-else>
						{{ selectedMedia.width }} x {{ selectedMedia.height }}
					</span>
				</p>

				<div v-if="selectedMedia.aggregate_type == 'video'">
					<h6 class="text-uppercase text-muted">Video Data</h6>
					<p class="small mb-4">
						Video Codec: {{ selectedMedia.video_codec }}<br>
						FPS: {{ selectedMedia.video_framerate }}<br>
						Bitrate: {{ selectedMedia.video_bitrate_mbps }}mbps<br>
						Color Space: {{ selectedMedia.video_color_space ? selectedMedia.video_color_space : '?' }}<br>
						Audio Codec: {{ selectedMedia.audio_codec ? selectedMedia.audio_codec : 'none' }}<br>
					</p>
				</div>
				<div v-if="selectedMedia.exif && selectedMedia.exif.Model">
					<h6 class="text-uppercase text-muted">Camera</h6>
					<p class="small mb-4">
						{{ selectedMedia.exif.Model }}
						<span v-if="selectedMedia.exif.Make">
							({{ selectedMedia.exif.Make }})
						</span>
					</p>
				</div>
				<div v-if="selectedMedia.exif && selectedMedia.exif.Software">
					<h6 class="text-uppercase text-muted">Software</h6>
					<p class="small mb-4">
						{{ selectedMedia.exif.Software }}
					</p>
				</div>

			</div>

			<div v-if="creativeAsset.organic_usage_term">
				<h6 class="text-uppercase text-muted">Organic Usage Term</h6>
				<p class="small mb-4">
					{{ creativeAsset.organic_usage_term }} days
				</p>
			</div>

			<div v-if="creativeAsset.paid_usage_term">
				<h6 class="text-uppercase text-muted">Paid Usage Term</h6>
				<p class="small mb-4">
					{{ creativeAsset.paid_usage_term }} days
				</p>
			</div>

			<div v-if="creativeAsset.expires_at">
				<h6 class="text-uppercase text-muted">Expiration Date</h6>
				<p class="small mb-4">
					{{ formatDate(creativeAsset.expires_at) }}
				</p>
			</div>

			<h6 class="text-uppercase text-muted">Client Visibility</h6>
			<p class="mb-4">
				<div v-if="this.selectedMedia.show_client">
					<a @click.prevent="toggleShowClient" class="btn btn-sm btn-warning px-4 py-2 rounded-pill" href="#">
						<i class="fe fe-eye me-2"></i> Visible
					</a>
				</div>
				<div v-else>
					<a @click.prevent="toggleShowClient" class="btn btn-sm btn-white px-4 py-2 rounded-pill text-muted" href="#">
						<i class="fe fe-eye-off me-2"></i> Hidden
					</a>
				</div>
			</p>

			<h6 class="text-uppercase text-muted">Client URL</h6>
			<div class="input-group input-group-sm mb-4">
				<input
					type="text"
					class="form-control form-control-sm text-secondary"
					readonly
					:value="this.clientUrl">
			</div>

			<h6 class="text-uppercase text-muted">Approval Status</h6>
			<p class="mb-4">
				<div v-if="creativeAsset.approved_media.length">
					<div class="btn btn-sm btn-success px-4 py-2 rounded-pill">
						<i class="fe fe-check-circle me-2"></i> Approved
					</div>
				</div>
				<div v-else>
					<div class="btn btn-sm btn-white px-4 py-2 rounded-pill text-muted">
						<i class="fe fe-circle me-2"></i> Unapproved
					</div>
				</div>
			</p>

		</div>

		<div class="col-12 col-lg-7 col-xl-8">

			<!-- Card -->
			<div class="card">
				<div class="card-body">

					<div v-if="isReplacing">
						<div class="mb-3 text-center ratio ratio-4x3 bg-secondary rounded">
							<div class="d-flex flex-column justify-content-center bg-white rounded text-start border p-4">

								<drag-drop :uppy="uppy"/>

								<div class="list-group list-group-flush bg-white mt-3">

									<div v-for="(file, index) in filesUploading" :key="file.id" class="list-group-item">
										<div class="row">
											<div class="col">
												<span class="fw-bold">{{ file.name }}</span>

												<span class="badge bg-light ms-2">
													{{ Math.round(file.progress.percentage) }}%
												</span>
												<br/>

												<small class="text-muted">{{ formatBytes(file.size) }}</small>
											</div>
										</div><!--/.row-->
										<div class="progress progress-sm mt-3">
											<div class="progress-bar" role="progressbar"
												:style="'width: ' + file.progress.percentage + '%'"
												:aria-valuenow="file.progress.percentage"
												aria-valuemin="0" aria-valuemax="100"></div>
										</div>
									</div><!--/.list-group-item-->

								</div><!--/.list-group-->

							</div><!--/d-flex-->
						</div><!--/.ratio-4x3-->
					</div><!--/v-if-->

					<!-- Media -->
					<div v-else-if="isEmbeddableImage">
						<div class="mb-3 bg-secondary rounded ratio ratio-1x1">
							<a class="w-100 h-100" @click.prevent="openImageLightbox('#media')" href="#">

								<img :src="selectedMedia.embeddable.url"
									:class="selectedMedia.embeddable.width >= selectedMedia.embeddable.height
										? 'position-absolute w-100 h-auto top-50 start-50 translate-middle'
										: 'position-absolute h-100 w-auto top-50 start-50 translate-middle'"
									id="media">
							</a>
						</div>
					</div>

					<div v-else-if="isEmbeddableVideo && selectedMedia.poster">
						<div class="mb-3 bg-secondary rounded ratio ratio-1x1">
							<a class="w-100 h-100" @click.prevent="openVideoLightbox('#media')" href="#">

								<img :src="selectedMedia.poster.url" id="media"
									:class="selectedMedia.poster.width >= selectedMedia.poster.height
										? 'position-absolute w-100 h-auto top-50 start-50 translate-middle'
										: 'position-absolute h-100 w-auto top-50 start-50 translate-middle'">

								<img src="/img/play-overlay.png" style="width:20%;height:auto;"
									class="position-absolute top-50 start-50 translate-middle">

							</a>
						</div>
					</div>

					<div v-else-if="isEmbeddableVideo && !this.selectedMedia.poster">
						<div class="mb-3 text-center ratio ratio-1x1 bg-secondary rounded">
							<a class="w-100 h-100" @click.prevent="openVideoLightbox('#media')" href="#">

								<img src="/img/play-overlay.png" style="width:20%;height:auto;"
									class="position-absolute top-50 start-50 translate-middle">

							</a>
						</div>
					</div>

					<div v-else-if="selectedMedia.aggregate_type == 'audio'">
						<div class="mb-3 text-center ratio ratio-1x1 bg-secondary rounded">
							<div class="d-flex flex-column align-items-center justify-content-center">
								<div class="text-white mb-3"><i class="fe fe-music"></i></div>
								<div class="fw-bold text-white">Audio</div>
							</div>
						</div>
					</div>

					<div v-else-if="selectedMedia.processing_job_status == 'failed'">
						<div class="mb-3 text-center ratio ratio-1x1 bg-secondary rounded">
							<div class="d-flex flex-column align-items-center justify-content-center">
								<div class="text-white mb-3"><i class="fe fe-eye-off"></i></div>
								<div class="fw-bold text-white">Could not create embeddable media</div>
							</div>
						</div>
					</div>

					<div v-else>
						<div class="mb-3 text-center ratio ratio-1x1 bg-secondary rounded">
							<div class="d-flex flex-column align-items-center justify-content-center">
								<div class="spinner-border text-white mb-3" role="status">
									<span class="visually-hidden">Loading...</span>
								</div>
								<div class="fw-bold text-white">Processing media...</div>
							</div>
						</div>
					</div>

					<div class="row">
						<div class="col">
							<a @click.prevent="isReplacing = !isReplacing" class="btn btn-lg btn-white w-100 rounded-pill" href="#">
								<i class="fe fe-refresh-cw me-1"></i>
								Replace with a New Version
							</a>
						</div>
						<div class="col">
							<a :href="creativeAssetEndpoint + '/media/' + selectedMedia.id + '/download'" class="btn btn-lg btn-white w-100 rounded-pill">
								<i class="fe fe-download me-1"></i>
								Download Original <span class="text-muted">({{ formatBytes(selectedMedia.size) }})</span>
							</a>
						</div>
					</div><!--/.row-->

					<div v-if="creativeAsset.title || creativeAsset.caption">

						<div class="row">
							<div class="col">
								<hr class="my-4">
							</div>
						</div>

						<div class="row">
							<div class="col">
								<div v-if="creativeAsset.title" class="h3">
									{{ creativeAsset.title }}
								</div>
								<div v-if="creativeAsset.caption"
									class="text-secondary"
									v-html="nl2br(linkify(creativeAsset.caption))"
								></div>
							</div><!--/.col-->
						</div><!--/.row-->

					</div><!--/v-if-->

				</div><!--/.card-body-->
			</div><!--/.card-->

		</div><!--/.col-->
	</div><!--/.row-->

	<admin-deliverable-creative-asset-comments
		:creative-asset-data="creativeAsset"
		:creative-asset-endpoint="creativeAssetEndpoint"
	></admin-deliverable-creative-asset-comments>

</template>

<script>
	import BigPicture from 'bigpicture';

	import AdminDeliverableCreativeAssetComments from './AdminDeliverableCreativeAssetComments.vue';

	import dayjs from 'dayjs';

	import Uppy from '@uppy/core';
	import AwsS3Multipart from '@uppy/aws-s3-multipart';
	import { DragDrop } from '@uppy/vue';


	import '@uppy/core/dist/style.min.css';
	import '@uppy/drag-drop/dist/style.min.css';
	import '@uppy/progress-bar/dist/style.min.css';


	export default {

		props: ['deliverableData', 'creativeAssetData', 'clientUrl', 'creativeAssetEndpoint'],

		components: {
			'drag-drop': DragDrop,
			'admin-deliverable-creative-asset-comments': AdminDeliverableCreativeAssetComments,
		},

		data() {
			return {
				deliverable: this.deliverableData,
				creativeAsset: this.creativeAssetData,
				selectedMedia: this.creativeAssetData.media[0],

				isReplacing: false,
				polling: null,

				filesUploading: [],
			}
		},

		mounted() {

			this.resetPolling();

			/*
			Register Uppy event listeners
			=================================*/
			this.uppy.on('file-added', (file) => {
				this.filesUploading.push(file);
			});

			this.uppy.on('upload', (data) => {
				// console.log('upload', data);
			});

			this.uppy.on('upload-progress', (file, progress) => {
				for (var i = 0; i < this.filesUploading.length; i++) {
					if(this.filesUploading[i].id == file.id) {
						this.filesUploading[i].progress.percentage = progress.bytesUploaded / progress.bytesTotal * 100;
					}
				}
			});

			this.uppy.on('upload-success', (file, response) => {
				this.handleUploadSuccess(file, response);
			});

			this.uppy.on('complete', (result) => {
				// console.log('complete');
				// console.log('successful files:', result.successful);
				// console.log('failed files:', result.failed);
			});

			this.uppy.on('error', (error) => {
				console.error(error);
			});

			this.uppy.on('upload-error', (file, error, response) => {
				console.log('upload-error');
				console.log('error with file:', file.id);
				console.log('error message:', error);
			});

		},

		computed: {

			uppy() {
				// initialize Uppy
				const uppy = new Uppy({
					// https://uppy.io/docs/uppy/
					id: 'uppyCreativeAsset' + this.creativeAsset.id,
					autoProceed: true,
					// allowMultipleUploadBatches: true,
					debug: true,
					inline: true,
					meta: {
						creativeAssetId: this.creativeAsset.id,
					},
					restrictions: {
						allowedFileTypes: this.allowedFileTypes,
						maxFileSize: 1000 * 1000 * 1000 * 1000 * 2, /* 2 GB */
						maxNumberOfFiles: 1,
						minNumberOfFiles: 1,
					}
				}).use(AwsS3Multipart, {
					limit: 5,
					companionUrl: '/',
					companionHeaders:
					{
						'X-CSRF-TOKEN': window.csrfToken,
					},
				});

				return uppy;
			},

			allowedFileTypes() {
				if(this.deliverable.type == 'video') {
					return ['video/*', 'audio/*'];
				}
				else if(this.deliverable.type == 'image') {
					return ['image/*'];
				}
				else {
					return ['image/*', 'video/*', 'audio/*'];
				}
			},

			isEmbeddableVideo() {
				if(this.selectedMedia.aggregate_type == 'video' && this.selectedMedia.embeddable) {
					return true;
				}
				return false;
			},

			isEmbeddableImage() {
				if(this.selectedMedia.aggregate_type == 'image' && this.selectedMedia.embeddable) {
					return true;
				}
				return false;
			},

		},

		methods: {

			copyClientUrl() {
				this.$refs.clientUrl.focus();
				document.execCommand('copy');
			},

			toggleShowClient() {
				axios.patch(this.creativeAssetEndpoint + '/media/' + this.selectedMedia.id + '/toggle-show-client')
					.then((response) => {
						this.selectedMedia.show_client = ! this.selectedMedia.show_client;
					})
					.catch((error) => {
						console.error(error);
					});
			},

			handleUploadSuccess(file, response) {

				// add file to media table in database
				axios.post(this.creativeAssetEndpoint + '/media', {
					original_name: file.name,
					size: file.size,
					extension: file.extension,
					key: file.s3Multipart.key
				})
				.then((response) => {

					this.creativeAsset.media = response.data;

					this.resetSelectedMedia();
					this.resetPolling();

					// remove from filesUploading array
					for (var i = 0; i < this.filesUploading.length; i++)
					{
						if(this.filesUploading[i].id == file.id) {
							this.filesUploading.splice(i, 1);
						}
					}

					this.isReplacing = false;
				})
				.catch((error) => {
					console.error(error);
				});

			},

			fetchMedia() {
				axios.get(this.creativeAssetEndpoint + '/media')
					.then((response) => {

						this.creativeAsset.media = response.data;

						this.resetSelectedMedia();

						if(this.isEmbeddableImage || this.isEmbeddableVideo) {
							clearInterval(this.polling); // won't be polled anymore
						}
					})
					.catch((error) => {
						console.error(error);
					});
			},

			resetPolling() {
				clearInterval(this.polling);
				if(!this.selectedMedia.embeddable && this.selectedMedia.processing_job_status != 'failed') {
					this.polling = setInterval(() => {
						this.fetchMedia()
					}, 3000) // check every 3 seconds
					setTimeout(() => { clearInterval(this.polling) }, 1000 * 60 * 30) //stop polling after 30 minutes
				}
			},

			resetSelectedMedia() {
				this.selectedMedia = this.creativeAsset.media[0];
			},

			openImageLightbox(el) {
				BigPicture({
					el: document.querySelector(el),
					imgSrc: this.selectedMedia.embeddable.url,
					noLoader: true,
				});
			},

			openVideoLightbox(el) {
				BigPicture({
					el: document.querySelector(el),
					vidSrc: this.selectedMedia.embeddable.url,
					noLoader: true,
				});
			},

			formatDate(date) {
				return dayjs(date).format('MMM D, YYYY h:mma');
			},

			formatBytes(bytes, decimals = 0) {
				if (bytes === 0) return '0 bytes';
				const k = 1024;
				const dm = decimals < 0 ? 0 : decimals;
				const sizes = ['bytes', 'kb', 'mb', 'gb', 'tb', 'pb', 'eb', 'zb', 'yb'];
				const i = Math.floor(Math.log(bytes) / Math.log(k));
				return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
			},

			isLowRes(width, height) {
				if(width > 0 && height > 0 && (width * height < 1920 * 1080)) {
					return true;
				}
				else {
					return false;
				}
			},

			nl2br(str, is_xhtml) {
				var breakTag = (is_xhtml || typeof is_xhtml === 'undefined') ? '<br />'   : '<br>';
				return (str + '').replace(/([^>\r\n]?)(\r\n|\n\r|\r|\n)/g, '$1' + breakTag   + '$2');
			},

			linkify(text) {
				var urlRegex = /(https?:\/\/[^\s]+)/g;
				return text.replace(urlRegex, function(url) {
					return '<a href="' + url + '" target="_blank" rel="noopener noreferrer">' + url + '</a>';
				});
			},

			strLimit(str, length) {
				if(str.length <= length) {
					return str;
				}

				return str.substring(0, length) + "...";
			}

		},

		beforeDestroy () {
			clearInterval(this.polling);
			uppy.close();
		}
	}

</script>
